import React, { useState, useContext, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Profile from "../assets/images/profile_placeholder.png";
import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { UserNameValidation } from "../utils/validations";
import { Button } from "../components/ui/button";
import {
  User,
  Mail,
  Camera,
  ArrowLeft,
  Check,
  Loader2,
  Users,
  Car,
  CalendarCheck,
  DollarSign,
  Shield,
  CheckCircle2,
} from "lucide-react";

interface StaffDetail {
  name: string;
  email: string;
  password?: string;
  image: string;
}

const EditStaff: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { authState, staffMembers } = useContext(GlobalContext);
  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const [loader, setLoader] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [memberDetail, setMemberDetail] = useState<StaffDetail>({
    name: "",
    email: "",
    image: "",
  });

  const fetchStaffDetail = async () => {
    if (!id) return;
    try {
      const apiRes = await henceforthApi.Administrator.getStaffMemberDetail(id);
      const data = apiRes.data;
      setMemberDetail({
        name: data?.name || "",
        email: data?.email || "",
        image: data?.image || "",
      });
      if (Array.isArray(data?.roles)) {
        setSelectedRoles(data.roles);
      }
    } catch (err: any) {
      toast.error("Failed to load staff details");
    }
  };

  useEffect(() => {
    if (id) {
      fetchStaffDetail();
    }
  }, [id]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      return toast.error("You can upload image in JPG, PNG or WebP format");
    }

    if (file.size > 5 * 1024 * 1024) {
      return toast.error("Image file size must be less than 5MB");
    }

    try {
      setLoader(true);
      const apiRes = await henceforthApi.Auth.uploadImage("file", file);
      setMemberDetail((prev) => ({
        ...prev,
        image: apiRes.data.file_name,
      }));
      toast.success("Profile photo uploaded successfully");
    } catch (err: any) {
      toast.error(err?.response?.body?.error_description || "Image upload failed");
    } finally {
      setLoader(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setMemberDetail((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRoleToggle = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const formatRoleName = (role: string) => {
    return role
      .replace(/^ROLES_/i, "")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getRoleIcon = (role: string) => {
    const r = role.toLowerCase();
    if (r.includes("user")) return Users;
    if (r.includes("vehicle")) return Car;
    if (r.includes("booking")) return CalendarCheck;
    if (r.includes("transaction")) return DollarSign;
    return Shield;
  };

  const getRoleDescription = (role: string) => {
    const r = role.toLowerCase();
    if (r.includes("user")) return "Manage passenger & driver accounts";
    if (r.includes("vehicle")) return "Approve vehicle brands & models";
    if (r.includes("booking")) return "Monitor & track booked rides";
    if (r.includes("transaction")) return "View financial transactions & logs";
    return "Access to administrative module";
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = memberDetail.name.trim();
    const email = memberDetail.email.trim();

    if (!name) {
      return toast.warn("Please add a valid name");
    }
    if (!email) {
      return toast.warn("Please add a valid email");
    }
    if (!UserNameValidation(name)) {
      return toast.warn("Please enter alphabets only for the name");
    }
    if (!selectedRoles.length) {
      return toast.warn("Please select at least 1 role");
    }

    const items: any = {
      _id: id,
      name: name,
      email: email,
      language: "ENGLISH",
      roles: selectedRoles,
    };

    if (memberDetail.image) {
      items["image"] = memberDetail.image;
    }

    setSubmitting(true);
    henceforthApi.Administrator.addStaffMembers(items)
      .then((res: any) => {
        Swal.fire({
          title: "Profile Updated",
          text: res?.data?.message || "Staff profile updated successfully",
          icon: "success",
          confirmButtonColor: "#006747",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/staff/1/all");
        });
      })
      .catch((err: any) => {
        Swal.fire({
          title: "Error!",
          text: err?.response?.body?.error_description || "Failed to update staff member",
          icon: "error",
          confirmButtonColor: "#006747",
          confirmButtonText: "Close",
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const profileImg = memberDetail.image
    ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${memberDetail.image}`
    : Profile;

  return (
    <div className="w-full space-y-3 pb-6">
      {/* ── Main Form Layout: Compact 2-Column Split ── */}
      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        {/* ── Left Column (lg:col-span-4): Photo & Access Summary ── */}
        <div className="lg:col-span-4 space-y-4">
          {/* Photo Card */}
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs">
            <div className="mb-3">
              <h2 className="text-sm font-bold text-slate-800">Staff Photo</h2>
              <p className="text-xs text-slate-500">
                Staff profile picture for identification
              </p>
            </div>

            <div className="flex flex-col items-center justify-center py-3 bg-slate-50/60 rounded-xl border border-dashed border-slate-200">
              <div className="relative group">
                <div className="relative w-24 h-24 rounded-2xl overflow-hidden border-2 border-white shadow bg-white">
                  <img
                    src={profileImg}
                    alt={memberDetail.name || "Staff Avatar"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = Profile;
                    }}
                  />
                  {loader && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                    </div>
                  )}
                </div>

                {/* Camera Button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loader}
                  title="Change Photo"
                  className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-full bg-white text-[#006747] shadow-md flex items-center justify-center hover:bg-emerald-50 hover:scale-105 active:scale-95 transition border border-slate-200 cursor-pointer disabled:opacity-50"
                >
                  <Camera className="w-3.5 h-3.5" />
                </button>

                <input
                  ref={fileInputRef}
                  onChange={handleFile}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                />
              </div>

              <div className="mt-3 text-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loader}
                  className="text-xs h-7 px-2.5 gap-1.5"
                >
                  <Camera className="w-3 h-3" />
                  Change Photo
                </Button>
                <p className="text-[11px] text-slate-400 mt-1.5">
                  JPG, PNG, WebP up to 5MB
                </p>
              </div>
            </div>
          </div>

          {/* Access Preview Summary Card */}
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs">
            <div className="flex items-center justify-between mb-2.5">
              <h2 className="text-sm font-bold text-slate-800">Assigned Access</h2>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-[#006747] border border-emerald-200">
                {selectedRoles.length} Active
              </span>
            </div>

            {selectedRoles.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-1">
                No module permissions selected yet. Choose permissions from the right.
              </p>
            ) : (
              <div className="space-y-1.5">
                {selectedRoles.map((role) => {
                  const Icon = getRoleIcon(role);
                  return (
                    <div
                      key={role}
                      className="flex items-center gap-2 py-1 px-2 rounded-lg bg-slate-50 border border-slate-100 text-xs font-medium text-slate-700"
                    >
                      <Icon className="w-3.5 h-3.5 text-[#006747] shrink-0" />
                      <span className="truncate">{formatRoleName(role)}</span>
                      <Check className="w-3 h-3 text-emerald-600 ml-auto shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Right Column (lg:col-span-8): Unified Form Card ── */}
        <div className="lg:col-span-8 bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs space-y-5">
          {/* Section 1: Credentials */}
          <div>
            <div className="pb-3 mb-4 border-b border-slate-100">
              <h2 className="text-sm font-bold text-slate-800">Account Credentials</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Staff member identification details
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-slate-700">
                    Full Name <span className="text-rose-500">*</span>
                  </label>
                  <span className="text-[11px] text-slate-400 font-mono">
                    {memberDetail.name.length}/30
                  </span>
                </div>
                <div className="relative flex items-center">
                  <User className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
                  <input
                    type="text"
                    required
                    maxLength={30}
                    name="name"
                    value={memberDetail.name}
                    onChange={handleChange}
                    placeholder="e.g. John Doe"
                    className="w-full pl-9 pr-3.5 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-800 focus:outline-none focus:border-[#006747] focus:ring-2 focus:ring-[#006747]/20 transition"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Email Address <span className="text-rose-500">*</span>
                </label>
                <div className="relative flex items-center">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
                  <input
                    type="email"
                    required
                    maxLength={50}
                    name="email"
                    value={memberDetail.email}
                    onChange={handleChange}
                    placeholder="e.g. staff@readyshare.com"
                    className="w-full pl-9 pr-3.5 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-800 focus:outline-none focus:border-[#006747] focus:ring-2 focus:ring-[#006747]/20 transition"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Permissions */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 pb-3 mb-3 border-b border-slate-100">
              <div>
                <h2 className="text-sm font-bold text-slate-800">
                  Module Permissions <span className="text-rose-500">*</span>
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Select which dashboard sections this staff member is authorized to manage
                </p>
              </div>
              <span className="self-start sm:self-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-[#006747] border border-emerald-200">
                {selectedRoles.length} Selected
              </span>
            </div>

            {/* Permission Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {Array.isArray(staffMembers) &&
                staffMembers.map((role: string) => {
                  const isChecked = selectedRoles.includes(role);
                  const Icon = getRoleIcon(role);
                  const desc = getRoleDescription(role);

                  return (
                    <label
                      key={role}
                      className={`relative flex items-start gap-3 p-4 rounded-xl border text-left transition cursor-pointer select-none ${isChecked
                        ? "bg-emerald-50/70 border-[#006747] ring-1 ring-[#006747]/30 shadow-2xs"
                        : "bg-white border-slate-200/90 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleRoleToggle(role)}
                        className="w-4 h-4 mt-0.5 rounded text-[#006747] border-slate-300 focus:ring-[#006747] accent-[#006747] cursor-pointer shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <Icon className={`w-3.5 h-3.5 ${isChecked ? "text-[#006747]" : "text-slate-500"}`} />
                          <span className={`text-xs font-bold ${isChecked ? "text-[#006747]" : "text-slate-800"}`}>
                            {formatRoleName(role)}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                          {desc}
                        </p>
                      </div>
                      {isChecked && (
                        <CheckCircle2 className="w-4 h-4 text-[#006747] shrink-0" />
                      )}
                    </label>
                  );
                })}
            </div>
          </div>

          {/* Section 3: Action Buttons directly inside Card Footer */}
          <div className="pt-4 border-t border-slate-100 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto text-xs h-9"
              onClick={() => window.history.back()}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="w-full sm:w-auto text-xs h-9"
              disabled={submitting || loader || !memberDetail.name || !memberDetail.email}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  Saving Changes...
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditStaff;
